import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';

import { InputError } from '@atoms/InputError/InputError';

type Keys = 'day' | 'month' | 'year';

type State = {
  [key in Keys]?: string;
};

type Props = {
  onChange: (value: string) => void;
  defaultValue?: string;
  errorMessage?: string;
  'data-testid'?: string;
};

export const SeparatedDateField = ({
  onChange,
  errorMessage,
  defaultValue,
  'data-testid': dataTestId
}: Props) => {
  const [date, setDate] = useState<State>({
    day: undefined,
    month: undefined,
    year: undefined
  });

  // useEffect used for loading receivers
  useEffect(() => {
    if (defaultValue) {
      setDate({
        day: defaultValue.slice(8, 10),
        month: defaultValue.slice(5, 7),
        year: defaultValue.slice(0, 4)
      });
    }
  }, [defaultValue]);

  const onFieldChange = (field: Keys, e: ChangeEvent<HTMLInputElement>) => {
    const updatedDate = { ...date, [field]: e.target?.value };
    setDate(updatedDate);

    const { day, month, year } = updatedDate;

    // Only call onChange if all fields are fully filled
    if (year?.length === 4 && month?.length === 2 && day?.length === 2) {
      onChange(`${year}-${month}-${day}`);
    }
  };

  return (
    <>
      <div className="grid grid-cols-3 gap-x-4" data-testid={dataTestId}>
        <input
          placeholder="DD"
          defaultValue={date.day}
          className="h-12 w-full rounded-2xl p-4 shadow-plain focus:shadow-plain-lg group-hover:shadow-plain-lg"
          onChange={(e) => onFieldChange('day', e)}
        />
        <input
          placeholder="MM"
          defaultValue={date.month}
          className="h-12 w-full rounded-2xl p-4 shadow-plain focus:shadow-plain-lg group-hover:shadow-plain-lg"
          onChange={(e) => onFieldChange('month', e)}
        />
        <input
          placeholder="YYYY"
          defaultValue={date.year}
          className="h-12 w-full rounded-2xl p-4 shadow-plain focus:shadow-plain-lg group-hover:shadow-plain-lg"
          onChange={(e) => onFieldChange('year', e)}
        />
        <span className="pl-2 text-xs text-content-subtle">Day</span>
        <span className="pl-2 text-xs text-content-subtle">Month</span>
        <span className="pl-2 text-xs text-content-subtle">Year</span>
      </div>

      <InputError errorMessage={errorMessage} />
    </>
  );
};
